import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { FUNDING_WORKSPACE_AGENCY_STORAGE_KEY } from '../utils/funding/constants';
import { useFundingRoutes } from '../hooks/funding/useFundingRoutes';
import { hasValidAgencyId } from '../utils/portalAccess';

const FundingWorkspaceContext = createContext(null);

export function FundingWorkspaceProvider({ children }) {
  const routes = useFundingRoutes();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();

  const workspaceAgencyId = useMemo(() => {
    if (routes.isAgencyContext) {
      return routes.agencyId;
    }
    if (routes.isPlatformContext) {
      return (
        searchParams.get('agency_id')
        || sessionStorage.getItem(FUNDING_WORKSPACE_AGENCY_STORAGE_KEY)
        || ''
      );
    }
    // Standalone /fundgenius-hq — derive from authenticated user
    if (hasValidAgencyId(user)) {
      return user.agency_id;
    }
    return null;
  }, [routes.isAgencyContext, routes.isPlatformContext, routes.agencyId, searchParams, user]);

  const setWorkspaceAgencyId = useCallback((agencyId) => {
    if (!routes.isPlatformContext) return;

    if (agencyId) {
      sessionStorage.setItem(FUNDING_WORKSPACE_AGENCY_STORAGE_KEY, agencyId);
    } else {
      sessionStorage.removeItem(FUNDING_WORKSPACE_AGENCY_STORAGE_KEY);
    }

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (agencyId) {
        next.set('agency_id', agencyId);
      } else {
        next.delete('agency_id');
      }
      return next;
    }, { replace: true });
  }, [routes.isPlatformContext, setSearchParams]);

  const workspaceLoading = authLoading && !user;
  const workspaceError = null;

  const value = useMemo(() => ({
    ...routes,
    workspaceAgencyId,
    setWorkspaceAgencyId,
    requiresAgencySelection: routes.isPlatformContext && !workspaceAgencyId,
    workspaceLoading,
    workspaceError,
    authUser: user,
  }), [routes, workspaceAgencyId, setWorkspaceAgencyId, workspaceLoading, workspaceError, user]);

  return (
    <FundingWorkspaceContext.Provider value={value}>
      {children}
    </FundingWorkspaceContext.Provider>
  );
}

export function useFundingWorkspace() {
  const context = useContext(FundingWorkspaceContext);
  if (!context) {
    throw new Error('useFundingWorkspace must be used within FundingWorkspaceProvider');
  }
  return context;
}
